import {
    Chart as ChartJS,
    LinearScale,
    PointElement,
    LineElement,
    TimeScale,
    Title,
    Tooltip,
} from 'chart.js';
import 'chartjs-adapter-date-fns';
import { Line } from 'react-chartjs-2';
import type { ChartData, ChartOptions } from 'chart.js';
import { R as ProgramResources } from '../../resources/ProgramsResources.resx';
import { format as formatString } from '@/Resources.resx';
import { axisArrowsPlugin } from './AxisArrowsPlugin';
import { Utils } from '@/shared/Utils';

ChartJS.register(
    LinearScale,
    PointElement,
    LineElement,
    TimeScale,
    Title,
    Tooltip,
    axisArrowsPlugin,
);

const primaryBlue = getComputedStyle(document.documentElement)
    .getPropertyValue('--color-primary-blue')
    .trim();

const gray300 = getComputedStyle(document.documentElement)
    .getPropertyValue('--color-gray-300')
    .trim();

const gray900 = getComputedStyle(document.documentElement)
    .getPropertyValue('--color-gray-900')
    .trim();

const options: ChartOptions<'line'> = {
    responsive: true,
    plugins: {
        tooltip: {
            callbacks: {
                title: () => '',
                label: (context) => {
                    const weight = context.parsed.y;
                    const date = new Date(context.parsed.x);
                    const formattedDate = Utils.formatLongDate(date);
                    return formatString(
                        ProgramResources.SundVaegtChartTooltip,
                        formattedDate,
                        weight.toString(),
                    );
                },
            },
        },
    },
    scales: {
        y: {
            border: {
                display: true,
                color: primaryBlue,
                width: 2,
            },
            grid: {
                color: gray300,
            },
            ticks: {
                color: gray900,
            },
            title: {
                font: {
                    size: 14,
                },
                display: true,
                color: gray900,
                text: ProgramResources.SundvaegtWeightLogChartYLabel,
            },
        },
        x: {
            type: 'time',
            time: {
                unit: 'month',
                displayFormats: { month: 'MMM' },
            },
            border: {
                display: true,
                color: primaryBlue,
                width: 2,
            },
            grid: {
                display: true,
                color: gray300,
            },
            ticks: {
                color: gray900,
                major: {
                    enabled: true,
                },
                callback: (val, index, ticks) => {
                    const date = new Date(Number(val));
                    const month = date.toLocaleString('default', {
                        month: 'short',
                    });
                    const year = date.getFullYear();

                    // Show year only for major ticks
                    return ticks[index].major || index == 0
                        ? `${month} ${year}`
                        : month;
                },
                maxTicksLimit: 20,
            },
            title: {
                font: {
                    size: 14,
                },
                display: true,
                color: gray900,
                text: ProgramResources.SundvaegtWeightLogChartXLabel,
            },
        },
    },
};

export type SimpleWeightLog = {
    x: number; // timestamp in milliseconds (Date.getTime())
    y: number; // weight in kg
};

interface LineChartProps {
    data: SimpleWeightLog[];
}

export const LineChart = ({ data }: LineChartProps) => {
    const getChartData = (): ChartData<'line'> => {
        const color = getComputedStyle(document.documentElement)
            .getPropertyValue('--color-secondary-green')
            .trim();

        return {
            datasets: [
                {
                    data: data,
                    borderColor: color,
                    pointBackgroundColor: color,
                    pointRadius: 4,
                    pointHoverRadius: 7,
                    tension: 0.5,
                },
            ],
        };
    };

    return <Line data={getChartData()} options={options} />;
};
